import { useState, useEffect, useCallback } from 'react';
import { DollarSign, TrendingUp, Calendar, RefreshCw, Percent, User, XCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS = [
  { value: '01', label: 'Ocak' },
  { value: '02', label: 'Şubat' },
  { value: '03', label: 'Mart' },
  { value: '04', label: 'Nisan' },
  { value: '05', label: 'Mayıs' },
  { value: '06', label: 'Haziran' },
  { value: '07', label: 'Temmuz' },
  { value: '08', label: 'Ağustos' },
  { value: '09', label: 'Eylül' },
  { value: '10', label: 'Ekim' },
  { value: '11', label: 'Kasım' },
  { value: '12', label: 'Aralık' }
];

const YEARS = ['all', '2024', '2025', '2026'];

export default function Commissions() {
  const { getAuthHeader, user, isAdmin } = useAuth();
  const [commissions, setCommissions] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  
  // Get current month and year for staff restriction
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear().toString();
  const currentMonth = (currentDate.getMonth() + 1).toString().padStart(2, '0');
  
  // Staff can only see current month, Admin can see all
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [loading, setLoading] = useState(true);
  const [exchangeRates, setExchangeRates] = useState({ usd: 35, eur: 38, gbp: 45 });

  const fetchCommissions = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/commissions`, getAuthHeader());
      setCommissions(response.data);
      
      if (response.data.length > 0 && !selectedUser) {
        if (isAdmin()) {
          // Admin sees all users, select the first one
          setSelectedUser(response.data[0].user_email);
        } else {
          // Staff user - they only get their own data from the API
          // So we can safely use the first (and only) item in the response
          setSelectedUser(response.data[0].user_email);
        }
        
        if (response.data[0].exchange_rates) {
          setExchangeRates(response.data[0].exchange_rates);
        }
      }
    } catch (error) {
      toast.error('Prim verileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, isAdmin, selectedUser]);

  useEffect(() => {
    fetchCommissions();
  }, [fetchCommissions]);

  const formatCurrency = (value, currency = 'TRY') => {
    const amount = Math.round(value || 0).toLocaleString('tr-TR');
    switch(currency) {
      case 'USD': return `$${amount}`;
      case 'EUR': return `€${amount}`;
      case 'GBP': return `£${amount}`;
      case 'TRY': 
      default: return `₺${amount}`;
    }
  };

  // Get selected user's data
  const selectedCommission = commissions.find(c => c.user_email === selectedUser);
  const allMonthlyData = selectedCommission?.monthly_data || [];
  
  // Filter by selected year and month
  const filteredMonthlyData = allMonthlyData.filter(m => {
    const monthParts = m.month?.split('-');
    const monthYear = monthParts?.[0];
    const monthNum = monthParts?.[1];
    
    // Handle "Bilinmeyen" months - show them when "all" is selected
    if (m.month === 'Bilinmeyen') {
      return selectedYear === 'all' && selectedMonth === 'all';
    }
    
    if (selectedYear !== 'all' && monthYear !== selectedYear) return false;
    if (selectedMonth !== 'all' && monthNum !== selectedMonth) return false;
    return true;
  });

  // Get unique years for filter
  const availableYears = [...new Set(allMonthlyData.map(m => m.month?.split('-')[0]).filter(Boolean))].sort().reverse();

  // Prepare chart data
  const chartData = filteredMonthlyData.map(m => ({
    month: m.month,
    prim: m.total_commission_try
  })).reverse();

  // Calculate totals for filtered data - BY CURRENCY
  const filteredTotals = filteredMonthlyData.reduce((acc, m) => {
    acc.sales += m.sales_count || 0;
    
    // Revenue by currency
    acc.revenue_try += m.payments_by_currency?.TRY || 0;
    acc.revenue_usd += m.payments_by_currency?.USD || 0;
    acc.revenue_eur += m.payments_by_currency?.EUR || 0;
    acc.revenue_gbp += m.payments_by_currency?.GBP || 0;
    
    // Commission by currency - Sales
    acc.sales_commission_try += m.sales_commission?.try || 0;
    acc.sales_commission_usd += m.sales_commission?.usd || 0;
    acc.sales_commission_eur += m.sales_commission?.eur || 0;
    acc.sales_commission_gbp += m.sales_commission?.gbp || 0;
    
    // Commission by currency - Reference
    acc.reference_commission_try += m.reference_commission?.try || 0;
    acc.reference_commission_usd += m.reference_commission?.usd || 0;
    acc.reference_commission_eur += m.reference_commission?.eur || 0;
    acc.reference_commission_gbp += m.reference_commission?.gbp || 0;
    
    // Commission by currency - ELSA FROST
    acc.elsa_frost_commission_try += m.elsa_frost_commission?.try || 0;
    acc.elsa_frost_commission_usd += m.elsa_frost_commission?.usd || 0;
    acc.elsa_frost_commission_eur += m.elsa_frost_commission?.eur || 0;
    acc.elsa_frost_commission_gbp += m.elsa_frost_commission?.gbp || 0;
    
    acc.total_commission_try += m.total_commission_try || 0;
    return acc;
  }, { 
    sales: 0, 
    revenue_try: 0, revenue_usd: 0, revenue_eur: 0, revenue_gbp: 0,
    sales_commission_try: 0, sales_commission_usd: 0, sales_commission_eur: 0, sales_commission_gbp: 0,
    reference_commission_try: 0, reference_commission_usd: 0, reference_commission_eur: 0, reference_commission_gbp: 0,
    elsa_frost_commission_try: 0, elsa_frost_commission_usd: 0, elsa_frost_commission_eur: 0, elsa_frost_commission_gbp: 0,
    total_commission_try: 0
  });
  
  // Calculate total revenue in TL
  const totalRevenueTL = filteredTotals.revenue_try + 
    (filteredTotals.revenue_usd * exchangeRates.usd) + 
    (filteredTotals.revenue_eur * exchangeRates.eur) + 
    (filteredTotals.revenue_gbp * exchangeRates.gbp);

  // Get all records for the table
  const allRecords = filteredMonthlyData.flatMap(m => 
    (m.records || []).map(r => ({
      ...r,
      month: m.month
    }))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="prim-loading">
        <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="prim-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div>
          <h2 className="text-2xl font-bold font-[Manrope] tracking-tight">Prim Takibi</h2>
          <p className="text-muted-foreground">
            {isAdmin() ? 'Personel prim verileri (Tedavi Tarihine Göre)' : 'Kişisel prim bilgileriniz'}
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          {isAdmin() && commissions.length > 0 && (
            <Select value={selectedUser} onValueChange={setSelectedUser}>
              <SelectTrigger className="w-[180px]" data-testid="user-select">
                <SelectValue placeholder="Personel seçin" />
              </SelectTrigger>
              <SelectContent>
                {commissions.map(c => (
                  <SelectItem key={c.user_email} value={c.user_email}>
                    {c.user_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          {/* Year/Month filter - Admin can change, Staff is locked to current month */}
          <Select 
            value={selectedYear} 
            onValueChange={setSelectedYear}
            disabled={!isAdmin()}
          >
            <SelectTrigger className="w-[120px]" data-testid="year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {isAdmin() && <SelectItem value="all">Tüm Yıllar</SelectItem>}
              <SelectItem value="2024">2024</SelectItem>
              <SelectItem value="2025">2025</SelectItem>
              <SelectItem value="2026">2026</SelectItem>
              <SelectItem value="2027">2027</SelectItem>
            </SelectContent>
          </Select>
          
          <Select 
            value={selectedMonth} 
            onValueChange={setSelectedMonth}
            disabled={!isAdmin()}
          >
            <SelectTrigger className="w-[120px]" data-testid="month-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {isAdmin() && <SelectItem value="all">Tüm Aylar</SelectItem>}
              {MONTHS.map(m => (
                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {!isAdmin() && (
            <span className="text-xs text-muted-foreground">
              (Sadece bulunduğunuz ayı görüntüleyebilirsiniz)
            </span>
          )}
          
          <Button variant="outline" onClick={fetchCommissions} className="btn-scale" data-testid="refresh-prim">
            <RefreshCw className="h-4 w-4 mr-2" />
            Yenile
          </Button>
        </div>
      </div>

      {commissions.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <DollarSign className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Prim Verisi Bulunamadı</h3>
            <p className="text-muted-foreground">
              Google Sheets verisi yüklendiğinde prim verileri burada görünecek
            </p>
          </div>
        </Card>
      ) : selectedCommission ? (
        <>
          {/* Aylık Ciro Özeti Kartı */}
          <Card className="card-hover" data-testid="monthly-revenue-card">
            <CardHeader>
              <CardTitle className="font-[Manrope] flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-green-500" />
                Aylık Ciro Özeti
              </CardTitle>
              <CardDescription>
                {selectedYear === 'all' ? 'Tüm yıllar' : selectedYear} / {selectedMonth === 'all' ? 'Tüm aylar' : MONTHS.find(m => m.value === selectedMonth)?.label}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {filteredTotals.revenue_try > 0 && (
                  <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-xl border border-blue-200 dark:border-blue-800">
                    <p className="text-xs text-muted-foreground mb-1">TL Ciro</p>
                    <p className="text-xl font-bold text-blue-600">₺{Math.round(filteredTotals.revenue_try).toLocaleString('tr-TR')}</p>
                  </div>
                )}
                {filteredTotals.revenue_usd > 0 && (
                  <div className="p-4 bg-green-50 dark:bg-green-950/30 rounded-xl border border-green-200 dark:border-green-800">
                    <p className="text-xs text-muted-foreground mb-1">USD Ciro</p>
                    <p className="text-xl font-bold text-green-600">${Math.round(filteredTotals.revenue_usd).toLocaleString('tr-TR')}</p>
                  </div>
                )}
                {filteredTotals.revenue_eur > 0 && (
                  <div className="p-4 bg-purple-50 dark:bg-purple-950/30 rounded-xl border border-purple-200 dark:border-purple-800">
                    <p className="text-xs text-muted-foreground mb-1">EUR Ciro</p>
                    <p className="text-xl font-bold text-purple-600">€{Math.round(filteredTotals.revenue_eur).toLocaleString('tr-TR')}</p>
                  </div>
                )}
                {filteredTotals.revenue_gbp > 0 && (
                  <div className="p-4 bg-amber-50 dark:bg-amber-950/30 rounded-xl border border-amber-200 dark:border-amber-800">
                    <p className="text-xs text-muted-foreground mb-1">GBP Ciro</p>
                    <p className="text-xl font-bold text-amber-600">£{Math.round(filteredTotals.revenue_gbp).toLocaleString('tr-TR')}</p>
                  </div>
                )}
              </div>
              <div className="mt-4 text-sm text-muted-foreground">
                Toplam Satış: <span className="font-semibold text-foreground">{filteredTotals.sales} adet</span>
              </div>
            </CardContent>
          </Card>

          {/* Prim Info Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Satış Primi */}
            <Card className="border-blue-200 dark:border-blue-900" data-testid="sales-prim-card">
              <CardHeader>
                <CardTitle className="font-[Manrope] flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-blue-500" />
                  Satış Primi (%{selectedCommission?.commission_rates?.sales_rate || 4})
                </CardTitle>
                <CardDescription>SATIŞ + SATIŞ 2. VİSİT üzerinden</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Total Commission Summary by Currency */}
                <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-2">Toplam Satış Primi:</p>
                  <div className="flex flex-wrap gap-3">
                    {filteredTotals.sales_commission_try > 0 && (
                      <Badge variant="outline" className="bg-white">₺{Math.round(filteredTotals.sales_commission_try).toLocaleString('tr-TR')}</Badge>
                    )}
                    {filteredTotals.sales_commission_usd > 0 && (
                      <Badge variant="outline" className="bg-white">${Math.round(filteredTotals.sales_commission_usd).toLocaleString('tr-TR')}</Badge>
                    )}
                    {filteredTotals.sales_commission_eur > 0 && (
                      <Badge variant="outline" className="bg-white">€{Math.round(filteredTotals.sales_commission_eur).toLocaleString('tr-TR')}</Badge>
                    )}
                    {filteredTotals.sales_commission_gbp > 0 && (
                      <Badge variant="outline" className="bg-white">£{Math.round(filteredTotals.sales_commission_gbp).toLocaleString('tr-TR')}</Badge>
                    )}
                  </div>
                </div>
                
                {filteredMonthlyData.length > 0 ? filteredMonthlyData.slice(0, 6).map((month, idx) => (
                  <div key={idx} className="border-b last:border-0 py-3">
                    <div className="flex justify-between items-center mb-2">
                      <Badge variant="outline">{month.month}</Badge>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                      <div>
                        <p className="text-xs text-muted-foreground">₺ TL</p>
                        <p className="font-medium">₺{Math.round(month.sales_commission?.try || 0).toLocaleString('tr-TR')}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">$ USD</p>
                        <p className="font-medium">${Math.round(month.sales_commission?.usd || 0).toLocaleString('tr-TR')}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">€ EUR</p>
                        <p className="font-medium">€{Math.round(month.sales_commission?.eur || 0).toLocaleString('tr-TR')}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">£ GBP</p>
                        <p className="font-medium">£{Math.round(month.sales_commission?.gbp || 0).toLocaleString('tr-TR')}</p>
                      </div>
                    </div>
                  </div>
                )) : (
                  <p className="text-muted-foreground text-center py-4">Seçili dönemde veri bulunamadı</p>
                )}
              </CardContent>
            </Card>

            {/* Referans Primi */}
            <Card className="border-purple-200 dark:border-purple-900" data-testid="reference-prim-card">
              <CardHeader>
                <CardTitle className="font-[Manrope] flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-purple-500" />
                  Referans Primi (%{selectedCommission?.commission_rates?.reference_rate || 5})
                </CardTitle>
                <CardDescription>REFERANS + REFERANS 2. VİSİT üzerinden</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Total Commission Summary by Currency */}
                <div className="mb-4 p-3 bg-purple-50 dark:bg-purple-950/30 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-2">Toplam Referans Primi:</p>
                  <div className="flex flex-wrap gap-3">
                    {filteredTotals.reference_commission_try > 0 && (
                      <Badge variant="outline" className="bg-white">₺{Math.round(filteredTotals.reference_commission_try).toLocaleString('tr-TR')}</Badge>
                    )}
                    {filteredTotals.reference_commission_usd > 0 && (
                      <Badge variant="outline" className="bg-white">${Math.round(filteredTotals.reference_commission_usd).toLocaleString('tr-TR')}</Badge>
                    )}
                    {filteredTotals.reference_commission_eur > 0 && (
                      <Badge variant="outline" className="bg-white">€{Math.round(filteredTotals.reference_commission_eur).toLocaleString('tr-TR')}</Badge>
                    )}
                    {filteredTotals.reference_commission_gbp > 0 && (
                      <Badge variant="outline" className="bg-white">£{Math.round(filteredTotals.reference_commission_gbp).toLocaleString('tr-TR')}</Badge>
                    )}
                  </div>
                </div>
                
                {filteredMonthlyData.length > 0 ? filteredMonthlyData.slice(0, 6).map((month, idx) => (
                  <div key={idx} className="border-b last:border-0 py-3">
                    <div className="flex justify-between items-center mb-2">
                      <Badge variant="outline">{month.month}</Badge>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                      <div>
                        <p className="text-xs text-muted-foreground">₺ TL</p>
                        <p className="font-medium">₺{Math.round(month.reference_commission?.try || 0).toLocaleString('tr-TR')}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">$ USD</p>
                        <p className="font-medium">${Math.round(month.reference_commission?.usd || 0).toLocaleString('tr-TR')}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">€ EUR</p>
                        <p className="font-medium">€{Math.round(month.reference_commission?.eur || 0).toLocaleString('tr-TR')}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">£ GBP</p>
                        <p className="font-medium">£{Math.round(month.reference_commission?.gbp || 0).toLocaleString('tr-TR')}</p>
                      </div>
                    </div>
                  </div>
                )) : (
                  <p className="text-muted-foreground text-center py-4">Seçili dönemde veri bulunamadı</p>
                )}
              </CardContent>
            </Card>

            {/* ELSA Primi */}
            <Card className="border-emerald-200 dark:border-emerald-900 lg:col-span-2" data-testid="elsa-prim-card">
              <CardHeader>
                <CardTitle className="font-[Manrope] flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-emerald-500" />
                  ELSA Primi (%{selectedCommission?.commission_rates?.elsa_frost_rate || 4})
                </CardTitle>
                <CardDescription>ELSA + ELSA 1. VİSİT + ELSA 2. VİSİT üzerinden</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Total Commission Summary by Currency */}
                <div className="mb-4 p-3 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-2">Toplam ELSA Primi:</p>
                  <div className="flex flex-wrap gap-3">
                    {filteredTotals.elsa_frost_commission_try > 0 && (
                      <Badge variant="outline" className="bg-white">₺{Math.round(filteredTotals.elsa_frost_commission_try).toLocaleString('tr-TR')}</Badge>
                    )}
                    {filteredTotals.elsa_frost_commission_usd > 0 && (
                      <Badge variant="outline" className="bg-white">${Math.round(filteredTotals.elsa_frost_commission_usd).toLocaleString('tr-TR')}</Badge>
                    )}
                    {filteredTotals.elsa_frost_commission_eur > 0 && (
                      <Badge variant="outline" className="bg-white">€{Math.round(filteredTotals.elsa_frost_commission_eur).toLocaleString('tr-TR')}</Badge>
                    )}
                    {filteredTotals.elsa_frost_commission_gbp > 0 && (
                      <Badge variant="outline" className="bg-white">£{Math.round(filteredTotals.elsa_frost_commission_gbp).toLocaleString('tr-TR')}</Badge>
                    )}
                  </div>
                </div>
                
                {filteredMonthlyData.length > 0 ? filteredMonthlyData.slice(0, 6).map((month, idx) => (
                  <div key={idx} className="border-b last:border-0 py-3">
                    <div className="flex justify-between items-center mb-2">
                      <Badge variant="outline">{month.month}</Badge>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                      <div>
                        <p className="text-xs text-muted-foreground">₺ TL</p>
                        <p className="font-medium">₺{Math.round(month.elsa_frost_commission?.try || 0).toLocaleString('tr-TR')}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">$ USD</p>
                        <p className="font-medium">${Math.round(month.elsa_frost_commission?.usd || 0).toLocaleString('tr-TR')}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">€ EUR</p>
                        <p className="font-medium">€{Math.round(month.elsa_frost_commission?.eur || 0).toLocaleString('tr-TR')}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">£ GBP</p>
                        <p className="font-medium">£{Math.round(month.elsa_frost_commission?.gbp || 0).toLocaleString('tr-TR')}</p>
                      </div>
                    </div>
                  </div>
                )) : (
                  <p className="text-muted-foreground text-center py-4">Seçili dönemde veri bulunamadı</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Chart */}
          <Card data-testid="prim-chart">
            <CardHeader>
              <CardTitle className="font-[Manrope]">Aylık Prim Grafiği</CardTitle>
              <CardDescription>Seçili dönem için aylık prim tutarları (TL)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="month" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                      <YAxis tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                      <Tooltip 
                        formatter={(value) => formatCurrency(value)}
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                      />
                      <Bar dataKey="prim" name="Prim" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-muted-foreground">
                    Seçili dönemde veri bulunamadı
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Patient Records Table */}
          <Card data-testid="records-table">
            <CardHeader>
              <CardTitle className="font-[Manrope] flex items-center gap-2">
                <User className="h-5 w-5" />
                Hasta Listesi
              </CardTitle>
              <CardDescription>
                {selectedCommission.user_name} - {selectedYear === 'all' ? 'Tüm yıllar' : selectedYear} / {selectedMonth === 'all' ? 'Tüm aylar' : MONTHS.find(m => m.value === selectedMonth)?.label} ({allRecords.length} kayıt)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border overflow-hidden max-h-[500px] overflow-y-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-muted/95 backdrop-blur">
                    <TableRow>
                      <TableHead>Hasta Adı</TableHead>
                      <TableHead>Tedavi Tarihi</TableHead>
                      <TableHead>Satış Türü</TableHead>
                      <TableHead className="text-right">Ödeme</TableHead>
                      <TableHead>Döviz</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allRecords.length > 0 ? allRecords.map((record, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">
                          {record.patient_name || '-'}
                        </TableCell>
                        <TableCell>{record.treatment_date}</TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline" 
                            className={
                              record.sales_type?.includes('SATIŞ') && !record.sales_type?.includes('REFERANS')
                                ? 'bg-blue-100 text-blue-700 border-blue-200'
                                : record.sales_type?.includes('REFERANS')
                                ? 'bg-purple-100 text-purple-700 border-purple-200'
                                : record.sales_type?.startsWith('ELSA')
                                ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                                : record.sales_type === 'RPT'
                                ? 'bg-red-100 text-red-700 border-red-200'
                                : ''
                            }
                          >
                            {record.sales_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(record.payment_amount, record.currency)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{record.currency}</Badge>
                        </TableCell>
                      </TableRow>
                    )) : (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                          Seçili dönemde kayıt bulunamadı
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              
              {/* Currency Totals */}
              {allRecords.length > 0 && (
                <div className="mt-4 p-4 bg-muted/30 rounded-lg">
                  <h4 className="font-medium mb-3">Döviz Bazlı Ciro Toplamları</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {['TRY', 'USD', 'EUR', 'GBP'].map(currency => {
                      const total = allRecords
                        .filter(r => r.currency === currency)
                        .reduce((sum, r) => sum + (r.payment_amount || 0), 0);
                      const totalInTry = currency === 'TRY' 
                        ? total 
                        : currency === 'USD' 
                        ? total * exchangeRates.usd 
                        : currency === 'EUR'
                        ? total * exchangeRates.eur
                        : total * exchangeRates.gbp;
                      
                      const symbol = currency === 'TRY' ? '₺' : currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '£';
                      
                      return total > 0 ? (
                        <div key={currency} className="p-3 bg-background rounded-lg border">
                          <p className="text-xs text-muted-foreground">{currency}</p>
                          <p className="font-bold text-lg">{symbol}{Math.round(total).toLocaleString('tr-TR')}</p>
                          {currency !== 'TRY' && (
                            <p className="text-xs text-green-600">≈ ₺{Math.round(totalInTry).toLocaleString('tr-TR')}</p>
                          )}
                        </div>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* İptal ve Kayıp Kazanç */}
          {selectedCommission?.cancelled?.count > 0 && (
            <Card className="border-red-300 dark:border-red-900" data-testid="cancelled-lost-revenue-card">
              <CardHeader>
                <CardTitle className="font-[Manrope] flex items-center gap-2 text-red-600">
                  <XCircle className="h-5 w-5" />
                  İptal ve Kayıp Kazanç
                </CardTitle>
                <CardDescription>
                  {selectedCommission.cancelled.count} adet iptal edilen tedavi
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Lost Revenue Summary */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  {selectedCommission.cancelled.lost_try > 0 && (
                    <div className="p-3 bg-red-50 dark:bg-red-950/20 rounded-lg border border-red-200">
                      <p className="text-xs text-red-500">₺ TL Kayıp</p>
                      <p className="font-bold text-lg text-red-600">₺{Math.round(selectedCommission.cancelled.lost_try).toLocaleString('tr-TR')}</p>
                    </div>
                  )}
                  {selectedCommission.cancelled.lost_usd > 0 && (
                    <div className="p-3 bg-red-50 dark:bg-red-950/20 rounded-lg border border-red-200">
                      <p className="text-xs text-red-500">$ USD Kayıp</p>
                      <p className="font-bold text-lg text-red-600">${Math.round(selectedCommission.cancelled.lost_usd).toLocaleString('tr-TR')}</p>
                    </div>
                  )}
                  {selectedCommission.cancelled.lost_eur > 0 && (
                    <div className="p-3 bg-red-50 dark:bg-red-950/20 rounded-lg border border-red-200">
                      <p className="text-xs text-red-500">€ EUR Kayıp</p>
                      <p className="font-bold text-lg text-red-600">€{Math.round(selectedCommission.cancelled.lost_eur).toLocaleString('tr-TR')}</p>
                    </div>
                  )}
                  {selectedCommission.cancelled.lost_gbp > 0 && (
                    <div className="p-3 bg-red-50 dark:bg-red-950/20 rounded-lg border border-red-200">
                      <p className="text-xs text-red-500">£ GBP Kayıp</p>
                      <p className="font-bold text-lg text-red-600">£{Math.round(selectedCommission.cancelled.lost_gbp).toLocaleString('tr-TR')}</p>
                    </div>
                  )}
                </div>

                {/* Total Lost Revenue in TRY */}
                <div className="p-4 bg-red-100 dark:bg-red-950/30 rounded-lg border border-red-300 mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium text-red-700 dark:text-red-400">Toplam Kayıp Kazanç (TL karşılığı)</span>
                    <span className="text-2xl font-bold text-red-600">
                      ₺{Math.round(selectedCommission.cancelled.lost_revenue_try).toLocaleString('tr-TR')}
                    </span>
                  </div>
                  {/* Lost Commission */}
                  <div className="flex justify-between items-center pt-2 border-t border-red-300">
                    <span className="font-medium text-red-700 dark:text-red-400">Kaybedilen Prim (TL karşılığı)</span>
                    <span className="text-xl font-bold text-red-600">
                      ₺{Math.round(selectedCommission.cancelled.lost_commission_try || 0).toLocaleString('tr-TR')}
                    </span>
                  </div>
                </div>

                {/* Lost Commission by Currency */}
                {selectedCommission.cancelled.lost_commission && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                    {selectedCommission.cancelled.lost_commission.try > 0 && (
                      <div className="p-3 bg-orange-50 dark:bg-orange-950/20 rounded-lg border border-orange-200">
                        <p className="text-xs text-orange-500">₺ Prim Kaybı</p>
                        <p className="font-bold text-lg text-orange-600">₺{Math.round(selectedCommission.cancelled.lost_commission.try).toLocaleString('tr-TR')}</p>
                      </div>
                    )}
                    {selectedCommission.cancelled.lost_commission.usd > 0 && (
                      <div className="p-3 bg-orange-50 dark:bg-orange-950/20 rounded-lg border border-orange-200">
                        <p className="text-xs text-orange-500">$ Prim Kaybı</p>
                        <p className="font-bold text-lg text-orange-600">${Math.round(selectedCommission.cancelled.lost_commission.usd).toLocaleString('tr-TR')}</p>
                      </div>
                    )}
                    {selectedCommission.cancelled.lost_commission.eur > 0 && (
                      <div className="p-3 bg-orange-50 dark:bg-orange-950/20 rounded-lg border border-orange-200">
                        <p className="text-xs text-orange-500">€ Prim Kaybı</p>
                        <p className="font-bold text-lg text-orange-600">€{Math.round(selectedCommission.cancelled.lost_commission.eur).toLocaleString('tr-TR')}</p>
                      </div>
                    )}
                    {selectedCommission.cancelled.lost_commission.gbp > 0 && (
                      <div className="p-3 bg-orange-50 dark:bg-orange-950/20 rounded-lg border border-orange-200">
                        <p className="text-xs text-orange-500">£ Prim Kaybı</p>
                        <p className="font-bold text-lg text-orange-600">£{Math.round(selectedCommission.cancelled.lost_commission.gbp).toLocaleString('tr-TR')}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* By Sales Type */}
                {Object.keys(selectedCommission.cancelled.by_type || {}).length > 0 && (
                  <div className="mb-6">
                    <p className="text-sm font-medium mb-3 text-muted-foreground">Satış Türüne Göre İptaller</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {Object.entries(selectedCommission.cancelled.by_type).map(([type, data]) => (
                        <div key={type} className="p-3 border rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <Badge variant="destructive" className="text-xs">{type}</Badge>
                            <span className="text-sm font-bold text-red-600">{data.count} adet</span>
                          </div>
                          <div className="text-xs text-muted-foreground space-y-1">
                            {data.try > 0 && <p>₺{Math.round(data.try).toLocaleString('tr-TR')}</p>}
                            {data.usd > 0 && <p>${Math.round(data.usd).toLocaleString('tr-TR')}</p>}
                            {data.eur > 0 && <p>€{Math.round(data.eur).toLocaleString('tr-TR')}</p>}
                            {data.gbp > 0 && <p>£{Math.round(data.gbp).toLocaleString('tr-TR')}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Cancelled Records Table */}
                <div className="rounded-md border border-red-200 overflow-auto max-h-[400px]">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-red-50 dark:bg-red-950/30">
                        <TableHead className="text-red-700">Hasta</TableHead>
                        <TableHead className="text-red-700">Tedavi Tarihi</TableHead>
                        <TableHead className="text-red-700">Satış Türü</TableHead>
                        <TableHead className="text-right text-red-700">Tutar</TableHead>
                        <TableHead className="text-red-700">Sebep</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(selectedCommission.cancelled.records || []).map((rec, idx) => (
                        <TableRow key={idx} className="bg-red-50/50 dark:bg-red-950/10">
                          <TableCell className="font-medium">{rec.patient_name || '-'}</TableCell>
                          <TableCell>{rec.treatment_date?.split(' ')[0] || '-'}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-red-100 text-red-700 border-red-200">
                              {rec.sales_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-medium text-red-600">
                            {formatCurrency(rec.payment_amount, rec.currency)}
                          </TableCell>
                          <TableCell>
                            <span className="text-xs text-red-500">{rec.cancel_reason || 'İPTAL'}</span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <Card className="p-12">
          <div className="text-center">
            <DollarSign className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Personel Seçin</h3>
            <p className="text-muted-foreground">Prim verilerini görüntülemek için bir personel seçin</p>
          </div>
        </Card>
      )}
    </div>
  );
}
